/**
 * HorrorCharacterChip — the shared labeled character chip (wireframe §02.1b).
 *
 * The SVG glyph stacked over the character's Korean name, both tinted with the
 * character's dedicated token color (CHARACTER_COLORS). Used wherever a player
 * must tell the 5 archetypes apart at a glance — race-track lane label, coin
 * hand, 내 선택 badge — so identity reads as glyph + name + color, not glyph
 * alone. (ZoneBoard renders icon + name inline in its bar-chart rows instead.)
 */

import type { Character } from "@cloudnest/horror-race-engine";
import { CHARACTER_COLORS, CHARACTER_LABELS } from "./character-display";
import { HorrorCharacterIcon } from "./HorrorCharacterIcon";

interface HorrorCharacterChipProps {
  readonly character: Character;
  readonly iconSize?: number;
  /** Name-label font size in px. */
  readonly labelSize?: number;
}

export function HorrorCharacterChip({
  character,
  iconSize = 28,
  labelSize = 11,
}: HorrorCharacterChipProps) {
  const color = CHARACTER_COLORS[character];
  return (
    <span
      data-testid="horror-char-chip"
      data-character={character}
      className="inline-flex flex-col items-center gap-0.5"
      style={{ color, lineHeight: 1.05 }}
    >
      <HorrorCharacterIcon
        character={character}
        size={iconSize}
        color={color}
      />
      <span
        data-testid="horror-char-chip-label"
        className="block font-bold"
        style={{
          color,
          fontFamily:
            '"Pretendard Variable", Pretendard, system-ui, -apple-system, sans-serif',
          fontSize: labelSize,
          fontVariantNumeric: "tabular-nums",
          lineHeight: `${labelSize}px`,
        }}
      >
        {CHARACTER_LABELS[character]}
      </span>
    </span>
  );
}
